import { SxProps, Theme } from "@mui/material";

export const activityDialogTitleRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1,
  pr: 0.5,
  px: { xs: 2.5, sm: 4 },
  pt: 2,
  pb: 1.5,
};

export const activityDialogTitleTextSx: SxProps<Theme> = {
  pr: 1,
  wordBreak: "break-word",
  display: "block",
};

/** Voice message recorder name under the dialog title */
export const activityDialogAuthorSx: SxProps<Theme> = {
  m: 0,
  mt: 0.5,
  fontSize: "0.875rem",
  lineHeight: 1.5,
  color: "text.secondary",
  fontWeight: 400,
};

export const activityDialogLoadingSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 4,
};

/** Dialog body: extra horizontal inset so text is not flush to the paper edge */
export const activityDialogContentSx: SxProps<Theme> = {
  px: { xs: 2.5, sm: 4 },
  py: 2.5,
};

/** Activity summary line above step content: muted, italic, accent border */
export const activityDialogDescriptionBoxSx: SxProps<Theme> = {
  maxWidth: 680,
  width: "100%",
  mx: "auto",
  mb: 2.5,
  pl: 2,
  py: 1.25,
  borderRadius: 0.5,
  borderLeft: "3px solid",
  borderLeftColor: "primary.main",
  bgcolor: "action.hover",
  boxSizing: "border-box",
};

export const activityDialogDescriptionTextSx: SxProps<Theme> = {
  m: 0,
  fontSize: "0.9375rem",
  lineHeight: 1.6,
  color: "text.secondary",
  fontStyle: "italic",
  whiteSpace: "pre-wrap",
};

/** Call plan detail: description uses full modal width (no dialog max-width cap). */
export const activityDialogDescriptionBoxPlanDetailSx: SxProps<Theme> = {
  ...activityDialogDescriptionBoxSx,
  maxWidth: "none",
  width: "100%",
  mx: 0,
};

/** Column of body paragraphs */
export const activityDialogDocumentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
  maxWidth: 680,
  width: "100%",
  mx: "auto",
  pt: 0.5,
  boxSizing: "border-box",
};

/** Full-width document column for call plan detail (not centered dialog width). */
export const activityDialogDocumentPlanDetailSx: SxProps<Theme> = {
  ...activityDialogDocumentSx,
  maxWidth: "none",
  width: "100%",
  mx: 0,
  alignSelf: "stretch",
};

/** Base paragraph: readable text only */
export const activityDialogParagraphSx: SxProps<Theme> = {
  m: 0,
  fontSize: "1rem",
  lineHeight: 1.65,
  whiteSpace: "pre-wrap",
  color: "text.primary",
};

/**
 * Question styling: green-700 on white (~5.7:1) for WCAG AA body text.
 * (Avoids left border; color differentiates questions from instructions.)
 */
export const activityDialogQuestionBodyGreen = "#15803d";

/**
 * Expected answers: blue-800-scale on white (~6.3:1) for WCAG AA.
 */
export const activityDialogExpectedAnswerBodyBlue = "#1e40af";

/** Question block: stacked question line, optional description, expected answers */
export const activityDialogQuestionBlockSx: SxProps<Theme> = {
  m: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  alignItems: "stretch",
};

/** Question (left ~70%) and expected answer(s) (right ~30%) on one row. */
export const activityDialogQuestionQaRowSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 7fr) minmax(0, 3fr)",
  columnGap: 2,
  rowGap: 0.5,
  alignItems: "start",
  width: "100%",
};

/**
 * Call plan detail: stack question and expected answers so each spans the full row width
 * (no side-by-side columns).
 */
export const activityDialogQuestionQaRowPlanDetailSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  alignSelf: "stretch",
  gap: 1,
  width: "100%",
};

export const activityDialogQuestionTextSx: SxProps<Theme> = {
  m: 0,
  fontSize: "1rem",
  lineHeight: 1.65,
  whiteSpace: "pre-wrap",
  color: activityDialogQuestionBodyGreen,
  minWidth: 0,
};

export const activityDialogExpectedAnswerColumnSx: SxProps<Theme> = {
  m: 0,
  fontSize: "1rem",
  lineHeight: 1.65,
  fontWeight: 400,
  color: activityDialogExpectedAnswerBodyBlue,
  whiteSpace: "pre-wrap",
  minWidth: 0,
};

export const activityDialogExpectedAnswersListSx: SxProps<Theme> = {
  m: 0,
  pl: 2.25,
  listStyleType: "disc",
};

export const activityDialogExpectedAnswerItemSx: SxProps<Theme> = {
  // Typography defaults to display:block; li must be list-item for markers to show.
  display: "list-item",
  m: 0,
  mt: 0.25,
  fontSize: "1rem",
  lineHeight: 1.55,
  fontWeight: 400,
  color: activityDialogExpectedAnswerBodyBlue,
  whiteSpace: "pre-wrap",
};

/** Question type / metadata on the line below the Q&A row. */
export const activityDialogQuestionTypeMetaSx: SxProps<Theme> = {
  m: 0,
  mt: 0.25,
  fontSize: "0.875rem",
  lineHeight: 1.5,
  fontWeight: 400,
  fontStyle: "italic",
  color: "text.secondary",
  whiteSpace: "pre-wrap",
};

export const activityDialogCollapsibleControlBarSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  width: "100%",
  cursor: "pointer",
  textAlign: "left",
  border: "none",
  margin: 0,
  padding: 0,
  backgroundColor: "transparent",
  WebkitTapHighlightColor: "transparent",
  font: "inherit",
  color: "inherit",
  borderRadius: 1,
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

export const activityDialogCollapsibleStickyHeaderBarSx: SxProps<Theme> = {
  ...activityDialogCollapsibleControlBarSx,
  position: "sticky",
  top: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  borderBottom: "1px solid",
  borderColor: "divider",
  py: 0.75,
  px: 0.5,
  mb: 1,
};

export const activityDialogCollapsibleExpandBarSx: SxProps<Theme> = {
  ...activityDialogCollapsibleControlBarSx,
  alignItems: "flex-start",
  py: 1,
  px: 0.5,
};

export const activityDialogCollapsibleChevronWrapSx: SxProps<Theme> = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  color: "text.secondary",
};

export const activityDialogCollapsibleChevronIconSx: SxProps<Theme> = {
  fontSize: 22,
};

export const activityDialogCollapsibleSpinnerRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 1,
};

export const activityDialogCollapsibleExpandContentColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};
